import { useCallback, useEffect, useRef } from 'react';
import { v7 as uuidv7 } from 'uuid';

import type { CreditNotice, CreditStatus } from '@/types/shared';
import { useCreditSettlementEvents } from './use-credit-settlement-events';

interface MastraMemoryClient {
  saveMessageToMemory: (params: {
    agentId: string;
    messages: Array<{
      id: string;
      role: 'assistant';
      threadId: string;
      resourceId: string;
      createdAt: Date;
      content: {
        format: 2;
        parts: [{ type: 'data-creditNotice'; data: CreditNotice }];
        metadata: {
          creditNoticeId: string;
          creditNoticeLevel: CreditNotice['level'];
          creditNoticeThreshold: number;
        };
      };
    }>;
  }) => Promise<unknown>;
}

interface UseCreditNoticeStreamOptions {
  agentId: string;
  threadId: string;
  resourceId: string;
  mastraClient: MastraMemoryClient;
  /** Whether an assistant turn is actively streaming; notices queue until it ends. */
  isBusy: boolean;
  onCreditStatus?: (status: CreditStatus) => void;
  /** Renders the notice into the surface's own conversation/message state. */
  onShow: (notice: CreditNotice) => void;
}

/**
 * Shared plumbing for showing in-conversation credit-threshold notices: dedupes by
 * noticeId, defers a notice that arrives mid-stream until the stream ends (so it never
 * interrupts an in-progress reply), and best-effort persists it to Mastra memory so it
 * survives a reload. Each assistant surface (site assistant, image assistant, ...) owns
 * its own message/conversation shape and passes `onShow` to append into it.
 */
export function useCreditNoticeStream({
  agentId,
  threadId,
  resourceId,
  mastraClient,
  isBusy,
  onCreditStatus,
  onShow,
}: UseCreditNoticeStreamOptions) {
  const renderedNoticeIdsRef = useRef(new Set<string>());
  const isBusyRef = useRef(isBusy);
  isBusyRef.current = isBusy;
  // Notices that arrived while a response was actively streaming — shown once the
  // stream ends instead of interrupting it, so a notice never lands mid-message.
  const pendingNoticesRef = useRef<CreditNotice[]>([]);

  useEffect(() => {
    if (isBusy || pendingNoticesRef.current.length === 0) return;

    const queued = pendingNoticesRef.current;
    pendingNoticesRef.current = [];
    queued.forEach(onShow);
  }, [isBusy, onShow]);

  const appendCreditNotice = useCallback(
    async (notice: CreditNotice) => {
      if (renderedNoticeIdsRef.current.has(notice.noticeId)) return;
      renderedNoticeIdsRef.current.add(notice.noticeId);

      if (isBusyRef.current) {
        pendingNoticesRef.current.push(notice);
      } else {
        onShow(notice);
      }

      try {
        await mastraClient.saveMessageToMemory({
          agentId,
          messages: [
            {
              id: uuidv7(),
              role: 'assistant',
              threadId,
              resourceId,
              createdAt: new Date(),
              content: {
                format: 2,
                parts: [{ type: 'data-creditNotice', data: notice }],
                metadata: {
                  creditNoticeId: notice.noticeId,
                  creditNoticeLevel: notice.level,
                  creditNoticeThreshold: notice.threshold,
                },
              },
            },
          ],
        });
      } catch {
        // Best-effort persistence; the notice remains visible in the active conversation.
      }
    },
    [agentId, resourceId, threadId, mastraClient, onShow],
  );

  const appendCreditNotices = useCallback(
    async (notices: CreditNotice[]) => {
      for (const notice of notices) await appendCreditNotice(notice);
    },
    [appendCreditNotice],
  );

  useCreditSettlementEvents({
    threadId,
    onCreditStatus,
    onNotices: appendCreditNotices,
  });

  /** Call when the active thread changes, so notices from the previous thread can replay. */
  const resetRenderedNotices = useCallback(() => {
    renderedNoticeIdsRef.current = new Set();
  }, []);

  /** Call while hydrating thread history, so a persisted notice doesn't replay live. */
  const markNoticeRendered = useCallback((noticeId: string) => {
    renderedNoticeIdsRef.current.add(noticeId);
  }, []);

  return { resetRenderedNotices, markNoticeRendered };
}
